import { IPreHeatRecipeEditable } from '../../types/recipes-types';
import * as Yup from 'yup';
import React from 'react';
import { FormikConfig } from 'formik';
import CycleSettings from '../../enums/cycle-setting.enum';
import i18n from '../../infrastructure/i18n';
import { POST_WELD_SETTINGS_LIMITS } from '../../components/PostWeldSettings/constants';
import { LATIN_ALPHANUMERIC_WITH_SPACES_REGEX } from '../../constants/common';
import { PREHEAT_SETTINGS_LIMITS } from '../../components/PreHeatSettings/constants';

export const preHeatRecipeInitialValues: IPreHeatRecipeEditable = {
  name: '',
  mode: CycleSettings.PreHeat,
  rate: undefined,
  finalTemp: undefined,
  soakUpper: undefined,
  soakLower: undefined,
};

export const preHeatRecipeValidationSchema: FormikConfig<IPreHeatRecipeEditable>['validationSchema'] = Yup.object({
  name: Yup.string()
    .required(i18n.t('errors.requiredValue'))
    .min(
      POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MIN,
      i18n.t('errors.stringValue.min', '', {
        maxChars: POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MIN,
        value: i18n.t('cycle.settings.postWeld.recipeName'),
      }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MAX,
      i18n.t('errors.stringValue.max', '', {
        maxChars: POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MAX,
        value: i18n.t('cycle.settings.postWeld.recipeName'),
      }),
    )
    .matches(LATIN_ALPHANUMERIC_WITH_SPACES_REGEX, i18n.t('errors.stringValue.sanitizeAlphaNumericChars')),
  mode: Yup.string().required('Required'),
  rate: Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      PREHEAT_SETTINGS_LIMITS.RATE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: PREHEAT_SETTINGS_LIMITS.RATE.MIN }),
    )
    .max(
      PREHEAT_SETTINGS_LIMITS.RATE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: PREHEAT_SETTINGS_LIMITS.RATE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
  finalTemp: Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      PREHEAT_SETTINGS_LIMITS.FINAL_TEMPERATURE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: PREHEAT_SETTINGS_LIMITS.FINAL_TEMPERATURE.MIN }),
    )
    .max(
      PREHEAT_SETTINGS_LIMITS.FINAL_TEMPERATURE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: PREHEAT_SETTINGS_LIMITS.FINAL_TEMPERATURE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
  soakUpper: Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      PREHEAT_SETTINGS_LIMITS.UPPER_HOLD_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: PREHEAT_SETTINGS_LIMITS.UPPER_HOLD_TOLERANCE.MIN }),
    )
    .max(
      PREHEAT_SETTINGS_LIMITS.UPPER_HOLD_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: PREHEAT_SETTINGS_LIMITS.UPPER_HOLD_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
  soakLower: Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      PREHEAT_SETTINGS_LIMITS.LOWER_HOLD_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: PREHEAT_SETTINGS_LIMITS.LOWER_HOLD_TOLERANCE.MIN }),
    )
    .max(
      PREHEAT_SETTINGS_LIMITS.LOWER_HOLD_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: PREHEAT_SETTINGS_LIMITS.LOWER_HOLD_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
});

export const fields: {
  key: keyof IPreHeatRecipeEditable;
  label: string;
  placeholder: string;
  inputType: React.InputHTMLAttributes<HTMLInputElement>['type'];
}[] = [
  {
    key: 'name',
    inputType: 'text',
    label: 'Name',
    placeholder: 'Enter new recipe name',
  },
  {
    key: 'rate',
    inputType: 'number',
    label: 'Rate',
    placeholder: 'Enter rate (rt)',
  },
  {
    key: 'finalTemp',
    inputType: 'number',
    label: 'Final Temperature',
    placeholder: 'Enter final temperature (ht)',
  },
  {
    key: 'soakUpper',
    inputType: 'number',
    label: 'Soak upper tolerance',
    placeholder: 'Enter soak upper tolerance (up)',
  },
  {
    key: 'soakLower',
    inputType: 'number',
    label: 'Soak lower tolerance',
    placeholder: 'Enter soak lower tolerance (lw)',
  },
];

export const PRE_HEAT_RECIPE_PATHS = {
  BASE: '/pre-heat-recipes',
  CREATE: 'create',
  EDIT_BASE: ':id',
};

export const EDIT_PRE_HEAT_RECIPE_PATHS = {
  EDIT: 'edit',
};

export const editPreHeatRecipeFullPath = `${PRE_HEAT_RECIPE_PATHS.BASE}/${PRE_HEAT_RECIPE_PATHS.EDIT_BASE}/${EDIT_PRE_HEAT_RECIPE_PATHS.EDIT}`;

export const undefinedIndex = -1;

export const stepsLimitCount = 10;
